import React, { useEffect, useRef, useState } from "react";
import ResponsiveLineChart from "components/widgets/widgetTypes/charts/LineChart";
import { ReactComponent as WaterPressureIcon } from "icons/indicators/water-pressure.svg";
import { ReactComponent as WaterLevelIcon } from "icons/indicators/water-level.svg";
import { ReactComponent as WaterTemperatureIcon } from "icons/indicators/water-temperature.svg";
import { timeFrames } from "data/translationArrays";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import generateTimeframeBounds from "components/widgets/widgetTypes/charts/generateTimeframeBounds";
import { Button } from "components/ui/button";
import { resolveAnalyticsSelection } from "helpers/analyticsSensors";
import {
  customDateRangeBounds,
  dateInputValue,
} from "helpers/analyticsTimeRange";
import {
  loadProjectMeasurementRange,
  mergeLatestSensorData,
  transformProjectSensorData,
} from "helpers/projectSensors";

const metricIcons = {
  flow: WaterLevelIcon,
  pressure: WaterPressureIcon,
  temperature: WaterTemperatureIcon,
};

const ToggleWidgetData = ({ valueType, onSave, categories }) => {
  const options = categories.map((category) => ({
    label: category.metricCode,
    visualLabel: category.displayName,
    Icon: metricIcons[category.metricCode] || WaterLevelIcon,
  }));

  const currentOptionIndex = Math.max(options.findIndex(
    (category) => category.label === (valueType[0] || "flow")
  ), 0);

  if (options.length < 2) return null;
  const { visualLabel, Icon } = options[currentOptionIndex] || options[0];

  const toggleOption = () => {
    const newIndex = (currentOptionIndex + 1) % options.length;
    const newOption = options[newIndex].label;
    onSave({ valueType: [newOption] });
  };

  return (
    <Button
      variant="outline"
      className="opacity-100 h-6  transition-all  group/2 flex @sm:space-x-2 items-center hover:!opacity-100 border-gray-200 dark:text-gray-500 p-1 text-gray-200  hover:text-gray-800 dark:hover:text-gray-100 rounded-full  "
      onClick={toggleOption}
    >
      <span className="@sm:inline-flex capitalize @sm:pl-0.5 hidden z-10 w-auto text-xs leading-none text-current">
        {visualLabel}
      </span>
      <Icon className=" group-hover:opacity-100 group-hover/2:opacity-100 size-3.5 text-current" />
    </Button>
  );
};

const AnalyticsLineChart = ({
  onRowSelectionChange,
  selectedRowIds,
  toggleDataSelector,
  data,
}) => {
  const initialOptions = {
    city: "",
    location: "",
    selectedTimeframe: "latest",
    valueType: [],
    sensors: [],
    categories: {
      pressure: {
        minValue: 0,
        maxValue: 1,
        dangerValue: 0.6,
        criticalValue: 0.8,
      },
      temperature: {
        minValue: 10,
        maxValue: 30,
        dangerValue: 20,
        criticalValue: 25,
      },
      flow: {
        minValue: 0,
        maxValue: 1,
        dangerValue: 0.6,
        criticalValue: 0.8,
      },
    },
    showLimits: false, //TODO True
  };
  

  const [selectedTimeframe, setSelectedTimeframe] = useState(
    timeFrames[0].value
  );
  const [lastSelectedDate, setLastSelectedDate] = useState(new Date());
  const [lineChartOptions, setLineChartOptions] = useState({
    ...initialOptions,
  });
  const [sensors, setSensors] = useState([]);
  const [metricOptions, setMetricOptions] = useState([]);
  const [historicalData, setHistoricalData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [customDates, setCustomDates] = useState(() => ({
    start: dateInputValue(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
    end: dateInputValue(),
  }));
  const currentMetricCode = lineChartOptions.valueType[0] || "";
  const selectedSensors = (Array.isArray(data) ? data : []).filter((sensor) =>
    selectedRowIds.includes(sensor?.id)
  );
  const selectedSensorIdsKey = selectedSensors
    .map((sensor) => sensor.id)
    .sort()
    .join("|");
  const projectId = selectedSensors[0]?.projectId || "";
  const requestStart = lineChartOptions.startDate
    ? new Date(lineChartOptions.startDate).toISOString()
    : "";
  const requestEnd = lineChartOptions.endDate
    ? new Date(lineChartOptions.endDate).toISOString()
    : "";
  const selectedSensorsRef = useRef(selectedSensors);
  selectedSensorsRef.current = selectedSensors;

  useEffect(() => {
    const selection = resolveAnalyticsSelection(
      data,
      selectedRowIds,
      currentMetricCode
    );
    setSensors(selection.sensorExternalIds);
    setMetricOptions(selection.metricOptions);
    setLineChartOptions((prevOptions) => ({
        ...prevOptions,
        sensors: selection.sensorExternalIds,
        valueType: selection.selectedMetric
          ? [selection.selectedMetric.metricCode]
          : [],
        metricMetadata: selection.selectedMetric,
        city: selection.city,
        location: selection.location,
    }));
  }, [currentMetricCode, data, selectedRowIds]);

  const updateOptions = (newOptions) => {
    setLineChartOptions((prevOptions) => ({
      ...prevOptions,
      ...newOptions,
      metricMetadata:
        newOptions.valueType
          ? metricOptions.find(
              (metric) => metric.metricCode === newOptions.valueType[0]
            ) || prevOptions.metricMetadata
          : prevOptions.metricMetadata,
    }));
  };

  const handleTimeframeChange = (value) => {
    const isCustomTimeframe = value === "custom";
    setLineChartOptions((prev) => ({
      ...prev,
      selectedTimeframe: value,
      customTimeframeEnabled: isCustomTimeframe,
    }));
    setSelectedTimeframe(value);
    const { start, end } = isCustomTimeframe
      ? customDateRangeBounds(customDates.start, customDates.end)
      : generateTimeframeBounds(value, new Date());
    if (value === "latest" || (start && end)) {
      updateOptions({ startDate: start, endDate: end });
    }
  };

  const handleCustomDateChange = (field, value) => {
    const nextDates = { ...customDates, [field]: value };
    setCustomDates(nextDates);
    const { start, end } = customDateRangeBounds(
      nextDates.start,
      nextDates.end
    );
    if (start && end) updateOptions({ startDate: start, endDate: end });
  };

  const handleViewLastAvailableData = () => {
    setSelectedTimeframe("latest");
    const { start, end } = generateTimeframeBounds("latest");
    updateOptions({
      selectedTimeframe: "latest",
      startDate: start,
      endDate: end,
    });
  };

  const availabletimeFrames = [
    ...timeFrames,
    { value: "custom", label: "Rango personalizado" },
  ];

  useEffect(() => {
    const now = new Date();
    const { start, end } = selectedTimeframe === "custom"
      ? customDateRangeBounds(customDates.start, customDates.end)
      : generateTimeframeBounds(selectedTimeframe, lastSelectedDate || now);
    if (selectedTimeframe === "latest" || (start && end)) {
      setLineChartOptions((prevOptions) => ({
        ...prevOptions,
        startDate: start,
        endDate: end,
      }));
    }
  }, [customDates.end, customDates.start, lastSelectedDate, selectedTimeframe]);

  useEffect(() => {
    if (
      selectedTimeframe === "latest" ||
      !currentMetricCode ||
      !requestStart ||
      !requestEnd
    ) {
      setHistoricalData(null);
      setHistoryLoading(false);
      setHistoryError("");
      return undefined;
    }

    const sensorsForRequest = selectedSensorsRef.current;
    if (!projectId || sensorsForRequest.length === 0) {
      setHistoricalData(null);
      setHistoryLoading(false);
      setHistoryError("");
      return undefined;
    }

    let active = true;
    setHistoryLoading(true);
    setHistoryError("");

    Promise.all(
      sensorsForRequest.map((sensor) =>
        loadProjectMeasurementRange(projectId, {
          sensorId: sensor.id,
          metric: currentMetricCode,
          from: requestStart,
          to: requestEnd,
        })
      )
    )
      .then((measurementGroups) => {
        if (!active) return;
        setHistoricalData(
          transformProjectSensorData({
            sensors: sensorsForRequest,
            measurements: measurementGroups.flat(),
          })
        );
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error fetching Analytics history:", error.message);
        setHistoricalData([]);
        setHistoryError("No se pudieron cargar las mediciones históricas");
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    currentMetricCode,
    projectId,
    requestEnd,
    requestStart,
    selectedSensorIdsKey,
    selectedTimeframe,
  ]);

  const chartData = historicalData === null
    ? data
    : mergeLatestSensorData(
        historicalData,
        selectedSensors,
        Number.MAX_SAFE_INTEGER
      );

  return (
    <>
      {data && data.length > 0 && sensors.length > 0 ? (
        <>
          <div className="absolute right-3 top-3 flex gap-x-2">
            {selectedTimeframe === "custom" && (
              <div className="flex gap-x-1">
                <label className="sr-only" htmlFor="analytics-start-date">
                  Fecha de inicio
                </label>
                <input
                  id="analytics-start-date"
                  type="date"
                  value={customDates.start}
                  max={customDates.end}
                  onChange={(event) =>
                    handleCustomDateChange("start", event.target.value)
                  }
                  className="h-6 rounded-full border border-gray-200 bg-white px-2 text-xs dark:border-gray-700 dark:bg-gray-950"
                />
                <label className="sr-only" htmlFor="analytics-end-date">
                  Fecha de fin
                </label>
                <input
                  id="analytics-end-date"
                  type="date"
                  value={customDates.end}
                  min={customDates.start}
                  onChange={(event) =>
                    handleCustomDateChange("end", event.target.value)
                  }
                  className="h-6 rounded-full border border-gray-200 bg-white px-2 text-xs dark:border-gray-700 dark:bg-gray-950"
                />
              </div>
            )}
            <Select
              value={lineChartOptions.selectedTimeframe}
              onValueChange={handleTimeframeChange}
            >
              <SelectTrigger className="h-6 rounded-full pr-1 text-xs capitalize">
                <SelectValue placeholder={`Selecciona una rango de tiempo`}>
                  {lineChartOptions.selectedTimeframe &&
                    availabletimeFrames.find(
                      (param) =>
                        param.value === lineChartOptions.selectedTimeframe
                    )?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {availabletimeFrames.map((option) => {
                  const value = option.value || option;
                  const display = option.label || option;
                  return (
                    <SelectItem
                      className="capitalize"
                      key={value}
                      value={value}
                    >
                      {display}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <ToggleWidgetData
              valueType={lineChartOptions.valueType}
              onSave={updateOptions}
              categories={metricOptions}
            />
          </div>
          {historyLoading ? (
            <div className="flex h-full items-center justify-center">
              <span>Cargando mediciones históricas…</span>
            </div>
          ) : historyError ? (
            <div className="flex h-full items-center justify-center">
              <span>{historyError}</span>
            </div>
          ) : (
            <ResponsiveLineChart
              isAnalytics={true}
              data={chartData}
              options={lineChartOptions}
              onRowSelectionChange={onRowSelectionChange}
              selectedRowIds={selectedRowIds}
              toggleDataSelector={toggleDataSelector}
              selectedTimeframe={selectedTimeframe}
              lastSelectedDate={lastSelectedDate}
              updateOptions={updateOptions}
              setLastSelectedDate={setLastSelectedDate}
              onViewLastAvailableData={handleViewLastAvailableData}
            />
          )}
        </>
      ) : (
        <div className="flex h-full items-center justify-center">
          <span>
            Selecciona uno o más sensores para ver los datos comparativos
          </span>
        </div>
      )}
    </>
  );
};

export default AnalyticsLineChart;
